import React, { JSX, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RegistrationResponse } from 'services/api-client';
import { RegistrationSuccessDialog } from 'components/EmailVerificationComponents';
import { useAuth } from 'hooks/AuthContext';
import { LoadingLabel, PasswordField } from './RegistrationFields';
import { companySizeOptions, industryOptions } from './registrationOptions';
import { resolveRegistrationError } from './registrationErrors';
import { EmployerRegistrationData } from './registrationTypes';
import { validateEmployerRegistration } from './registrationValidation';

export const EmployerRegistrationForm = (): JSX.Element => {
  const { apiClient } = useAuth();
  const [formData, setFormData] = useState<EmployerRegistrationData>({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    industry: '',
    companySize: '',
    companyDescription: '',
    websiteUrl: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<RegistrationResponse | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field: keyof EmployerRegistrationData, value: string): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (): Promise<void> => {
    const validationErrors = validateEmployerRegistration(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length !== 0) return;

    setLoading(true);
    try {
      const result = await apiClient.createEmployerWithVerification({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        companyName: formData.companyName,
        industry: formData.industry,
        companySize: formData.companySize,
        companyDescription: formData.companyDescription,
        websiteUrl: formData.websiteUrl || undefined,
        phone: formData.phone || undefined,
      });
      apiClient.setPendingEmailVerification(formData.email);
      setRegistrationResult(result);
      setShowSuccess(true);
    } catch (error) {
      setErrors(resolveRegistrationError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3}>
      <Box sx={{ p: 5 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            Join as an Employer
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create your company account to start hiring top talent
          </Typography>
        </Box>
        <Stack spacing={4}>
          <AccountSection
            formData={formData}
            errors={errors}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onToggleConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
            onChange={handleInputChange}
          />
          <CompanySection formData={formData} errors={errors} onChange={handleInputChange} />
          {errors.general && <Alert severity="error">{errors.general}</Alert>}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ py: 2 }}
          >
            {loading ? (
              <LoadingLabel text="Creating Company Account..." />
            ) : (
              'Create Company Account'
            )}
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link href="/login" sx={{ fontWeight: 600 }}>
                Sign in here
              </Link>
            </Typography>
          </Box>
        </Stack>
      </Box>
      {showSuccess && registrationResult && (
        <RegistrationSuccessDialog
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          email={registrationResult.email}
          userType="employer"
        />
      )}
    </Paper>
  );
};

function AccountSection(props: {
  formData: EmployerRegistrationData;
  errors: Record<string, string>;
  showPassword: boolean;
  showConfirmPassword: boolean;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
  onChange: (field: keyof EmployerRegistrationData, value: string) => void;
}): JSX.Element {
  return (
    <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Account Information
      </Typography>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={props.formData.email}
            onChange={e => props.onChange('email', e.target.value)}
            placeholder="company@example.com"
            error={!!props.errors.email}
            helperText={props.errors.email}
            required
          />
          <TextField
            fullWidth
            label="Username"
            value={props.formData.username}
            onChange={e => props.onChange('username', e.target.value.toLowerCase())}
            placeholder="company123"
            error={!!props.errors.username}
            helperText={props.errors.username}
            required
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <PasswordField
            label="Password"
            value={props.formData.password}
            error={props.errors.password}
            placeholder="Create a strong password"
            showPassword={props.showPassword}
            onToggle={props.onTogglePassword}
            onChange={value => props.onChange('password', value)}
          />
          <PasswordField
            label="Confirm Password"
            value={props.formData.confirmPassword}
            error={props.errors.confirmPassword}
            placeholder="Confirm your password"
            showPassword={props.showConfirmPassword}
            onToggle={props.onToggleConfirmPassword}
            onChange={value => props.onChange('confirmPassword', value)}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

function CompanySection(props: {
  formData: EmployerRegistrationData;
  errors: Record<string, string>;
  onChange: (field: keyof EmployerRegistrationData, value: string) => void;
}): JSX.Element {
  return (
    <Box sx={{ bgcolor: 'primary.50', p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Company Information
      </Typography>
      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Company Name"
          value={props.formData.companyName}
          onChange={e => props.onChange('companyName', e.target.value)}
          placeholder="Your Company Inc."
          error={!!props.errors.companyName}
          helperText={props.errors.companyName}
          required
        />
        <Stack direction="row" spacing={2}>
          <FormControl fullWidth error={!!props.errors.industry} required>
            <InputLabel>Industry</InputLabel>
            <Select
              value={props.formData.industry}
              onChange={e => props.onChange('industry', e.target.value)}
              label="Industry"
            >
              {industryOptions.map(industry => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </Select>
            {props.errors.industry && <FormHelperText>{props.errors.industry}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth error={!!props.errors.companySize} required>
            <InputLabel>Company Size</InputLabel>
            <Select
              value={props.formData.companySize}
              onChange={e => props.onChange('companySize', e.target.value)}
              label="Company Size"
            >
              {companySizeOptions.map(size => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
            {props.errors.companySize && (
              <FormHelperText>{props.errors.companySize}</FormHelperText>
            )}
          </FormControl>
        </Stack>
        <TextField
          fullWidth
          label="Company Description"
          multiline
          rows={4}
          value={props.formData.companyDescription}
          onChange={e => props.onChange('companyDescription', e.target.value)}
          placeholder="Tell us about your company, what you do, your mission, and what makes you unique..."
          error={!!props.errors.companyDescription}
          helperText={
            props.errors.companyDescription ||
            `${props.formData.companyDescription.length}/50 characters minimum`
          }
          required
        />
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            label="Website URL"
            type="url"
            value={props.formData.websiteUrl}
            onChange={e => props.onChange('websiteUrl', e.target.value)}
            placeholder="https://www.yourcompany.com"
            error={!!props.errors.websiteUrl}
            helperText={props.errors.websiteUrl || 'Optional'}
          />
          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            value={props.formData.phone}
            onChange={e => props.onChange('phone', e.target.value)}
            placeholder="+1 (555) 123-4567"
            error={!!props.errors.phone}
            helperText={props.errors.phone || 'Optional'}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
